import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { PasswordResetFormData } from '../../types/auth';
import './PasswordResetForm.css';

// Step 1: Email validation schema
const emailSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required')
});

// Step 2: Verification code and password schema
const resetSchema = Yup.object().shape({
  verificationCode: Yup.string()
    .required('Verification code is required')
    .length(6, 'Verification code must be 6 digits'),
  newPassword: Yup.string()
    .min(8, 'Password must be at least 8 characters long')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
    )
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwords must match')
    .required('Please confirm your password')
});

interface PasswordResetFormProps {
  onSuccess?: () => void;
  onBackToLogin?: () => void;
}

type ResetStep = 'email' | 'verification';

const PasswordResetForm: React.FC<PasswordResetFormProps> = ({
  onSuccess,
  onBackToLogin
}) => {
  const { forgotPassword, confirmPassword, isLoading, error, clearError } = useAuth();
  const [currentStep, setCurrentStep] = useState<ResetStep>('email');
  const [userEmail, setUserEmail] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleEmailSubmit = async (
    values: { email: string },
    { setSubmitting, setFieldError }: any
  ) => {
    try {
      clearError();
      setSuccessMessage(null);

      const result = await forgotPassword(values.email);

      if (result.success) {
        setUserEmail(values.email);
        setSuccessMessage(result.message || 'Verification code sent to your email.');
        setTimeout(() => {
          setCurrentStep('verification');
          setSuccessMessage(null);
        }, 2000);
      } else {
        if (result.message?.includes('email')) {
          setFieldError('email', result.message);
        }
      }
    } catch (err) {
      console.error('Forgot password error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetSubmit = async (
    values: PasswordResetFormData & { confirmPassword: string },
    { setSubmitting, setFieldError }: any
  ) => {
    try {
      clearError();
      setSuccessMessage(null);

      const resetData: PasswordResetFormData = {
        email: userEmail,
        verificationCode: values.verificationCode,
        newPassword: values.newPassword
      };

      const result = await confirmPassword(resetData);

      if (result.success) {
        setSuccessMessage(result.message || 'Password reset successful! Redirecting to login...');
        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
          }, 2000);
        }
      } else {
        if (result.message?.includes('code')) {
          setFieldError('verificationCode', result.message);
        } else if (result.message?.includes('password')) {
          setFieldError('newPassword', result.message);
        }
      }
    } catch (err) {
      console.error('Password reset error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="password-reset-form-container">
      <div className="password-reset-form-card">
        <div className="form-header">
          <h2>Reset Your Password</h2>
          <p>
            {currentStep === 'email'
              ? 'Enter your email to receive a verification code and regain access to your speech development account'
              : 'Enter the verification code and create a new password'}
          </p>
        </div>

        {successMessage && (
          <div className="success-message" role="alert">
            <div className="success-icon">✓</div>
            <div className="success-text">{successMessage}</div>
          </div>
        )}

        {error && (
          <div className="error-message" role="alert">
            <div className="error-icon">⚠</div>
            <div className="error-text">{error}</div>
          </div>
        )}

        {currentStep === 'email' ? (
          <Formik
            initialValues={{ email: '' }}
            validationSchema={emailSchema}
            onSubmit={handleEmailSubmit}
          >
            {({ isSubmitting, touched, errors }) => (
              <Form className="password-reset-form">
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email Address *
                  </label>
                  <Field
                    type="email"
                    id="email"
                    name="email"
                    className={`form-input ${touched.email && errors.email ? 'error' : ''}`}
                    placeholder="Enter your email address"
                    disabled={isLoading || isSubmitting}
                  />
                  <ErrorMessage name="email" component="div" className="field-error" />
                </div>

                <button
                  type="submit"
                  className={`submit-button ${isLoading || isSubmitting ? 'loading' : ''}`}
                  disabled={isLoading || isSubmitting}
                >
                  {isLoading || isSubmitting ? (
                    <>
                      <span className="loading-spinner"></span>
                      Sending Code...
                    </>
                  ) : (
                    'Send Verification Code'
                  )}
                </button>
              </Form>
            )}
          </Formik>
        ) : (
          <Formik
            initialValues={{
              email: userEmail,
              verificationCode: '',
              newPassword: '',
              confirmPassword: ''
            }}
            validationSchema={resetSchema}
            onSubmit={handleResetSubmit}
          >
            {({ isSubmitting, touched, errors }) => (
              <Form className="password-reset-form">
                <div className="form-group">
                  <label htmlFor="verificationCode" className="form-label">
                    Verification Code *
                  </label>
                  <Field
                    type="text"
                    id="verificationCode"
                    name="verificationCode"
                    className={`form-input ${touched.verificationCode && errors.verificationCode ? 'error' : ''}`}
                    placeholder="Enter 6-digit code from your email"
                    maxLength="6"
                    disabled={isLoading || isSubmitting}
                  />
                  <ErrorMessage name="verificationCode" component="div" className="field-error" />
                </div>

                <div className="form-group">
                  <label htmlFor="newPassword" className="form-label">
                    New Password *
                  </label>
                  <Field
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    className={`form-input ${touched.newPassword && errors.newPassword ? 'error' : ''}`}
                    placeholder="Create a strong password"
                    disabled={isLoading || isSubmitting}
                  />
                  <ErrorMessage name="newPassword" component="div" className="field-error" />
                  <div className="password-requirements">
                    <small>
                      Password must contain at least 8 characters with uppercase, lowercase, number, and special character.
                    </small>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword" className="form-label">
                    Confirm Password *
                  </label>
                  <Field
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    className={`form-input ${touched.confirmPassword && errors.confirmPassword ? 'error' : ''}`}
                    placeholder="Confirm your password"
                    disabled={isLoading || isSubmitting}
                  />
                  <ErrorMessage name="confirmPassword" component="div" className="field-error" />
                </div>

                <button
                  type="submit"
                  className={`submit-button ${isLoading || isSubmitting ? 'loading' : ''}`}
                  disabled={isLoading || isSubmitting}
                >
                  {isLoading || isSubmitting ? (
                    <>
                      <span className="loading-spinner"></span>
                      Resetting Password...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </button>

                <button
                  type="button"
                  className="back-button"
                  onClick={() => setCurrentStep('email')}
                  disabled={isLoading || isSubmitting}
                >
                  ← Back to Email
                </button>
              </Form>
            )}
          </Formik>
        )}

        <div className="form-footer">
          <p>
            Remember your password?{' '}
            <button
              type="button"
              className="link-button"
              onClick={onBackToLogin}
              disabled={isLoading}
            >
              Sign In
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetForm;